/**
 * URL search params shared by every CRO forecast tab.
 *
 * Every page in the feature reads (year, week, optional month-final
 * indicator) from the URL so deep-links + back/forward navigation always
 * land on the same view, and switching tabs preserves the date.
 */

import { isoWeekNumber } from "./format";

export interface CroForecastSearch {
  year?: number;
  week?: number;
  /** When set, the URL targets the "Month Final" snapshot for that
   *  fiscal-year-month (YYYY-MM). UI swaps the week selector for a month
   *  display in this mode. */
  monthFinal?: string;
  /** Forecast Meeting drawer: the rep_id currently selected. */
  rep?: string;
  /**
   * Summary tab: which KPI tile's drill-down drawer is open. Kept in the URL so
   * a drill-down is deep-linkable and survives back/forward, same as `rep`.
   */
  tile?: CroTile;
  /**
   * Narrows a `pipeline_*` drill-down to one stage — set when a stage tile was
   * the entry point. Free-form because stage names come from HubSpot and change
   * without a deploy; validated only for length so a hostile URL can't bloat
   * the query string.
   */
  stage?: string;
  /** ``true`` → EntityDrawer renders in expanded (full-page) mode. */
  expanded?: boolean;
}

/**
 * Summary tiles that support a drill-down drawer.
 *
 * The three `pipeline_*` values cover twelve tiles: each row of three (total
 * pipeline / open deals / avg deal) describes the SAME deal set, so one value
 * serves the whole row, and the drawer header shows all three numbers so
 * whichever tile was clicked, that number is present. A Sales *stage* tile uses
 * `pipeline_sales` plus the `stage` param to narrow the same list.
 */
export const CRO_TILES = [
  "closed_month",
  "closed_quarter",
  "forecast_month",
  "forecast_quarter",
  "pipeline_sales",
  "pipeline_partner",
  "pipeline_cs",
] as const;
export type CroTile = (typeof CRO_TILES)[number];

/** TanStack Router `validateSearch` for every CRO forecast route. */
export function validateCroForecastSearch(
  raw: Record<string, unknown>,
): CroForecastSearch {
  const out: CroForecastSearch = {};
  if (raw.year != null) {
    const n = Number(raw.year);
    if (Number.isFinite(n) && n >= 2024 && n <= 2099) out.year = n;
  }
  if (raw.week != null) {
    const n = Number(raw.week);
    if (Number.isFinite(n) && n >= 1 && n <= 53) out.week = n;
  }
  if (typeof raw.monthFinal === "string" && /^\d{4}-\d{2}$/.test(raw.monthFinal)) {
    out.monthFinal = raw.monthFinal;
  }
  if (typeof raw.rep === "string" && raw.rep.length > 0) {
    out.rep = raw.rep;
  }
  if (
    typeof raw.tile === "string" &&
    (CRO_TILES as readonly string[]).includes(raw.tile)
  ) {
    out.tile = raw.tile as CroTile;
  }
  if (typeof raw.stage === "string" && raw.stage.length > 0 && raw.stage.length <= 120) {
    out.stage = raw.stage;
  }
  if (raw.expanded === true || raw.expanded === "1" || raw.expanded === "true") {
    out.expanded = true;
  }
  return out;
}

/** The ISO (year, week) of a fiscal month's *end* — the last ISO week
 *  that contains the month's final day. This is the week the month-final
 *  snapshot (the "<MONTH> FINAL" view) is keyed to. */
export function monthFinalToIsoWeek(monthFinal: string): {
  year: number;
  week: number;
} {
  const [yRaw, mRaw] = monthFinal.split("-").map(Number);
  const y = yRaw ?? 0;
  const m = mRaw ?? 1;
  // ``new Date(y, m, 0)`` = day 0 of the *next* month = last day of month m.
  const lastDay = new Date(y, m, 0);
  return isoWeekNumber(lastDay);
}

/** Fill in defaults from "now" if the URL is missing year+week.
 *
 *  When ``monthFinal`` is set the returned (year, week) is overridden to the
 *  month-end ISO week, so every tab's data hook automatically loads that
 *  month's final snapshot — the page components don't need to special-case
 *  month-final mode. */
export function resolveSearch(search: CroForecastSearch): {
  year: number;
  week: number;
  monthFinal?: string;
} {
  if (search.monthFinal) {
    const mf = monthFinalToIsoWeek(search.monthFinal);
    return { year: mf.year, week: mf.week, monthFinal: search.monthFinal };
  }
  const fallback = isoWeekNumber(new Date());
  return {
    year: search.year ?? fallback.year,
    week: search.week ?? fallback.week,
    monthFinal: search.monthFinal,
  };
}
